import { useEffect, useRef, useState } from 'react';

import { NativeSelect } from '@/components/ui/select';
import { api } from '../../lib/api';

export interface HgSousThemeQcmRow {
  id: string;
  libelle: string;
  position: number;
  nombre_exercices: number;
  theme_id: string;
  theme_code: string;
  theme_libelle: string;
  theme_discipline: string;
  niveau_id: string;
  niveau_code: string;
}

interface HgSousThemeSelecteurProps {
  niveauCode?: string | null;
  valeur: string | null;
  onChange: (sousThemeId: string) => void;
}

export function libelleSousTheme(sousTheme: HgSousThemeQcmRow): string {
  return `${sousTheme.theme_libelle} — ${sousTheme.libelle}`;
}

export function HgSousThemeSelecteurVue({
  sousThemes,
  valeur,
  onChange,
}: {
  sousThemes: HgSousThemeQcmRow[];
  valeur: string | null;
  onChange: (sousThemeId: string) => void;
}) {
  if (sousThemes.length === 0)
    return (
      <p className="text-muted-foreground text-sm">
        Aucun sous-thème avec des QCM disponibles pour le moment.
      </p>
    );
  if (sousThemes.length === 1)
    return <p className="text-muted-foreground text-sm">{libelleSousTheme(sousThemes[0]!)}</p>;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-muted-foreground text-[13px]" htmlFor="hg-sous-theme">
        Sous-thème
      </label>
      <NativeSelect
        id="hg-sous-theme"
        className="h-11 min-h-[44px]"
        value={valeur ?? ''}
        onChange={(event) => onChange(event.target.value)}
      >
        {sousThemes.map((sousTheme) => (
          <option key={sousTheme.id} value={sousTheme.id}>
            {libelleSousTheme(sousTheme)}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

export function HgSousThemeSelecteur({
  niveauCode = null,
  valeur,
  onChange,
}: HgSousThemeSelecteurProps) {
  const [sousThemes, setSousThemes] = useState<HgSousThemeQcmRow[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  /* onChange et valeur sont lus par référence : les mettre en dépendance de
     l'effet relancerait la requête à chaque rendu du parent. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valeurRef = useRef(valeur);
  valeurRef.current = valeur;

  useEffect(() => {
    let annule = false;
    setSousThemes(null);
    setErreur(null);
    void (async () => {
      try {
        const data = await api.miniApps.hgCollege.qcm.sousThemes.query({ niveauCode });
        if (annule) return;
        setSousThemes(data);
        const courant = valeurRef.current;
        const premier = data[0];
        if (premier && !data.some((sousTheme) => sousTheme.id === courant)) {
          onChangeRef.current(premier.id);
        }
      } catch (e) {
        if (!annule) setErreur(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      annule = true;
    };
  }, [niveauCode]);

  if (erreur) return <p role="alert" className="text-destructive text-sm">{erreur}</p>;
  if (sousThemes === null)
    return <p role="status" className="text-muted-foreground text-sm">Chargement des sous-thèmes…</p>;

  return (
    <HgSousThemeSelecteurVue sousThemes={sousThemes} valeur={valeur} onChange={onChange} />
  );
}
